import { useTranslation } from 'react-i18next'
import { cn } from 'src/helpers/style'

import {
  AppIcon,
  AppIconNames,
  Typography,
  TypographyVariant,
} from 'src/components/atoms'

import { getVisiblePageNumbers, VISIBLE_PAGE_BUTTON_COUNT } from './helpers'

export interface IPaginationProps {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
  className?: string
}

export const Pagination = ({
  currentPage,
  totalPages,
  onChange,
  className,
}: IPaginationProps): JSX.Element | null => {
  const { t } = useTranslation()

  if (totalPages <= 1) {
    return null
  }

  const visiblePages = getVisiblePageNumbers(totalPages, currentPage)
  const isFirst = currentPage === 1
  const isLast = currentPage === totalPages

  const handlePrev = (): void => {
    if (!isFirst) {
      onChange(currentPage - 1)
    }
  }

  const handleNext = (): void => {
    if (!isLast) {
      onChange(currentPage + 1)
    }
  }

  const previousLabel = t('pagination.previous')
  const nextLabel = t('pagination.next')

  const pageStripWidthClass =
    totalPages >= VISIBLE_PAGE_BUTTON_COUNT ? 'w-[5.25rem]' : 'w-[3.5rem]'

  return (
    <nav
      className={cn('flex items-center gap-6', className)}
      aria-label={t('pagination.ariaLabel')}
    >
      <button
        type="button"
        onClick={handlePrev}
        disabled={isFirst}
        aria-label={previousLabel}
        className={cn(
          'flex items-center gap-2 rounded-[30px] transition-opacity',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page',
          isFirst ? 'cursor-not-allowed' : 'hover:opacity-80',
        )}
      >
        <AppIcon
          name={AppIconNames.arrowLeft}
          className={cn(
            'size-4 shrink-0',
            isFirst ? 'text-content-secondary' : 'text-content-primary',
          )}
        />
        <Typography
          variant={TypographyVariant.bodySmStrong}
          className={
            isFirst ? 'text-content-secondary' : 'text-content-primary'
          }
        >
          {previousLabel}
        </Typography>
      </button>

      <ul
        className={cn(
          'inline-flex shrink-0 justify-center',
          pageStripWidthClass,
        )}
      >
        {visiblePages.map(page => {
          const isActive = page === currentPage
          return (
            <li key={page}>
              <button
                type="button"
                onClick={() => onChange(page)}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex size-7 items-center justify-center rounded-[12px] pb-[8px] pl-[9px] pr-[10px] pt-[7px] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page',
                  isActive ? 'bg-content-primary' : 'bg-transparent',
                )}
              >
                <Typography
                  variant={TypographyVariant.bodyXxsStrong}
                  className={cn(
                    'text-center',
                    isActive ? 'text-surface-raised' : 'text-content-secondary',
                  )}
                >
                  {page}
                </Typography>
              </button>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={handleNext}
        disabled={isLast}
        aria-label={nextLabel}
        className={cn(
          'flex items-center gap-2 rounded-[30px] transition-opacity',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page',
          isLast ? 'cursor-not-allowed' : 'hover:opacity-80',
        )}
      >
        <Typography
          variant={TypographyVariant.bodySmStrong}
          className={isLast ? 'text-content-secondary' : 'text-content-primary'}
        >
          {nextLabel}
        </Typography>
        <AppIcon
          name={AppIconNames.arrowRight}
          className={cn(
            'size-4 shrink-0',
            isLast ? 'text-content-secondary' : 'text-content-primary',
          )}
        />
      </button>
    </nav>
  )
}
